import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  useNotify
} from '@globaleris/ui.library'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumDispatchState, EnumDispatchType } from '../../../../api/oData'
import { enumToSelectOptionArray } from '../../../../helpers'
import { generateIconsFromConfiguration } from '../../../../helpers/dataTableActionIconHelper'
import useRetries from '../../../../hooks/useRetries'
import { ActionIcons, DatePeriod, GlobalNotification } from '../../../../model'
import {
  getCustomerDomain,
  RootStoreModel,
  storeDataTableRowsPerPageForCurrentPage
} from '../../../../redux'
import { CommunicationSendsDownloadMode, DispatchModel } from './comunicationSends.model'
import {
  checkDispatchesState,
  fetchSendsListItems,
  filterApplicableActionsConfiguration
} from './comunicationSends.service'

import { Card, Menu, MenuItem, Typography } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'
import { useSelector } from 'react-redux'
import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import { fileDownloadHelper } from '../../../../helpers/fileDownloadHelper'
import { usePageState } from '../../../../hooks'
import { useHttpError } from '../../../../hooks/useHttpError'
import { downloadFiles } from '../../../documents/documents.service'
import { mapSendToDownloadRequest } from './comunicationSends.mapper'

export interface ComunicationSendsPageProps {
  onNotificationChange: (notificacion: GlobalNotification | undefined) => void
  onFetchingChange: (boolean: boolean) => void
  dateRange: DatePeriod
}

export const ComunicationSendsPage: React.FC<ComunicationSendsPageProps> = ({
  onNotificationChange,
  onFetchingChange,
  dateRange
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'comunicationSends', 'enums', 'documents'])

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )

  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const { dataTableRowsPerPage } = usePageState()

  const { showError, clearError, errorProps } = useHttpError()
  const { info, error } = useNotify()

  const [dispatches, setDispatches] = useState<DispatchModel[] | undefined>(undefined)
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = React.useState(null)
  const [openMenu, setOpenMenu] = React.useState<boolean>(false)

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)

  const [isFetching, setIsFetching] = useState<boolean>(true)

  const [downloadOptions, setDownloadOptions] = React.useState<any[]>([])

  const fetchDispatches = React.useCallback(
    async (dateRange: DatePeriod): Promise<DispatchModel[] | undefined> => {
      setIsFetching(true)
      setDispatches(undefined)

      return await fetchSendsListItems(dateRange).then(response => {
        setIsFetching(false)

        if (response.success) {
          clearError()
          if (response.data !== undefined) {
            return response.data?.map((item: DispatchModel) => {
              return completeDataTableActions(item)
            })
          } else {
            return []
          }
        } else {
          response.error &&
            showError({
              errorCode: response.error.code,
              onRefresh: async () => {
                setDispatches(await fetchDispatches(dateRange))
              }
            })
          return []
        }
      })
    },
    [showError, clearError]
  )

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async (pendingDispatches: DispatchModel[]) => {
      pendingDispatches = await monitorDispatches(pendingDispatches)

      if (pendingDispatches?.length === 0) {
        stopRetriesTimer()
        setGlobalNotification(undefined)
        return
      }
    },
    5000,
    {
      immediate: true,
      onRetry: count => {
        setGlobalNotification({
          message: t('comunicationSends:globalNotifications.retries.messages.onRetry'),
          actions: [
            {
              label: t('comunicationSends:globalNotifications.retries.actions.cancel'),
              onClick: () => {
                stopRetriesTimer()
                setGlobalNotification({
                  message: t('comunicationSends:globalNotifications.retries.messages.onStop'),
                  actions: [
                    {
                      label: t('comunicationSends:globalNotifications.retries.actions.refresh'),
                      onClick: async () => {
                        setGlobalNotification(undefined)
                        setDispatches(await fetchDispatches(dateRange))
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        setGlobalNotification({
          message: t('comunicationSends:globalNotifications.retries.messages.onFinish'),
          actions: [
            {
              label: t('comunicationSends:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                setGlobalNotification(undefined)
                setDispatches(await fetchDispatches(dateRange))
              }
            },
            {
              label: t('comunicationSends:globalNotifications.retries.actions.close'),
              onClick: () => {
                setGlobalNotification(undefined)
              }
            }
          ]
        })
      },
      onError: error => {
        setGlobalNotification({
          message: t('comunicationSends:globalNotifications.retries.messages.onError'),
          actions: [
            {
              label: t('comunicationSends:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                setGlobalNotification(undefined)
                setDispatches(await fetchDispatches(dateRange))
              }
            }
          ]
        })
      }
    }
  )

  useEffect(() => {
    const getDispatches = async () => {
      setDispatches(await fetchDispatches(dateRange))
    }

    getDispatches()
    setGlobalNotification(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, activeBalanceZone, activeCompany])

  useEffect(() => {
    onFetchingChange(isFetching)
  }, [isFetching, onFetchingChange])

  useEffect(() => {
    if (dispatches) {
      isRetriesTimerActive && stopRetriesTimer()
      startRetriesTimer(
        dispatches?.filter(
          dispatch =>
            EnumDispatchState.Finalizado !== dispatch.state &&
            EnumDispatchState.Error !== dispatch.state
        )
      )
    }
  }, [dispatches])

  useEffect(() => {
    onNotificationChange(globalNotification)
  }, [globalNotification, onNotificationChange])

  useEffect(() => {
    setOpenMenu(true)
  }, [downloadOptions])

  const onDownloadActionHandler = (row: DispatchModel, event: any) => {
    let downloadButtons: any[] = []
    if (row.urlRequest && row.urlRequest != null) {
      downloadButtons.push({
        label: t('comunicationSends:dataTable.customActions.downloadRequest'),
        mode: CommunicationSendsDownloadMode.Request,
        url: row.urlRequest,
        dispatchId: row.id
      })
    }
    if (row.urlResponse && row.urlResponse != null) {
      downloadButtons.push({
        label: t('comunicationSends:dataTable.customActions.downloadResponse'),
        mode: CommunicationSendsDownloadMode.Response,
        url: row.urlResponse,
        dispatchId: row.id
      })
    }
    if (row.urlResponseConciliation && row.urlResponseConciliation != null) {
      downloadButtons.push({
        label: t('comunicationSends:dataTable.customActions.downloadResponseConciliation'),
        mode: CommunicationSendsDownloadMode.Conciliation,
        url: row.urlResponseConciliation,
        dispatchId: row.id
      })
    }

    setDownloadOptions(downloadButtons)

    setDownloadMenuAnchorEl(event.currentTarget)
  }

  const dataTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'dischargeDate',
      label: t('comunicationSends:dataTable.columns.sendDate'),
      type: DataType.TEXT
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'dispatchType',
      label: t('comunicationSends:dataTable.columns.sendType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumDispatchType, 'enums:EnumDispatchType')
    },
    {
      name: 'fileName',
      label: t('comunicationSends:dataTable.columns.fileName'),
      type: DataType.TEXT
    },
    {
      name: 'state',
      label: t('comunicationSends:dataTable.columns.state'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumDispatchState, 'enums:EnumDispatchState')
    }
  ]

  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    selectableRows: false,
    customInsertEditModal: true,
    rowsPerPage: dataTableRowsPerPage ?? 10,
    analytics: {
      customer: getCustomerDomain(),
      view: t('comunicationSends:dataTable.analytics')
    },
    onRowEdit: async (row: any) => {
      return true
    },
    onRowInsert: async () => {
      return true
    },
    onRowsDelete: async (rowsToDelete: any[] | undefined) => {},
    rowCustomActions: [
      {
        icon: <GetAppIcon fontSize="small" />,
        tooltip: t('comunicationSends:dataTable.customActions.download'),
        onClick: onDownloadActionHandler,
        visibleIf: 'isDownloadVisible'
      }
    ],
    onRowsPerPageChange: (rowsPerPage: number) => {
      storeDataTableRowsPerPageForCurrentPage(rowsPerPage)
    }
  }

  const monitorDispatches = async (pendingDispatches: DispatchModel[]) => {
    let currentDispatches = dispatches
    if (
      currentDispatches &&
      currentDispatches.length > 0 &&
      pendingDispatches &&
      pendingDispatches.length > 0
    ) {
      return await checkDispatchesState(
        pendingDispatches?.map(dispatch => dispatch.id),
        [EnumDispatchState.Finalizado, EnumDispatchState.Error]
      ).then(changedDispatches => {
        changedDispatches.forEach(changedDispatch => {
          if (currentDispatches) {
            const index = currentDispatches.findIndex(x => x.id === changedDispatch.id)

            currentDispatches = [
              ...currentDispatches!.slice(0, index),
              completeDataTableActions({ ...changedDispatch }),
              ...currentDispatches!.slice(index + 1)
            ]
            setDispatches(currentDispatches)

            const pendingIndex = pendingDispatches.findIndex(x => x.id === changedDispatch.id)

            pendingDispatches = [
              ...pendingDispatches!.slice(0, pendingIndex),
              ...pendingDispatches!.slice(pendingIndex + 1)
            ]
          }
        })
        return pendingDispatches
      })
    } else {
      return []
    }
  }

  const completeDataTableActions = (item: DispatchModel): DispatchModel => {
    return {
      ...item,
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfiguration(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([])
        )
    }
  }

  const onDownloadMenuCloseHandler = () => {
    setDownloadMenuAnchorEl(null)
    setOpenMenu(false)
  }

  const onDownloadMenuClickHandler = async (
    dispatchId: number,
    mode: CommunicationSendsDownloadMode
  ) => {
    const request = mapSendToDownloadRequest(dispatchId, mode)
    await downloadFiles(request).then(response => {
      if (response.success && response.data) {
        fileDownloadHelper(response.data.name, response.data)
      } else {
        error(t('documents:notifications.downloadFiles.error'))
      }
    })
    onDownloadMenuCloseHandler()
  }

  return (
    <Card raised>
      <DataTable
        data={dispatches}
        columns={dataTableColumns}
        options={{
          ...dataTableOptions,
          analytics: {
            customer: getCustomerDomain(),
            view: t('comunicationSends:dataTable.analytics')
          }
        }}
        info={DataTableErrorHandler(errorProps)}
        //userPermissions={getResourceUserPermissions(Resources.ComunicationsCenterSends)}
      />
      <Menu
        keepMounted={false}
        elevation={2}
        anchorEl={downloadMenuAnchorEl}
        open={Boolean(downloadMenuAnchorEl)}
        onClose={onDownloadMenuCloseHandler}
      >
        {downloadOptions.map((downloadOption, index) => (
          <MenuItem
            dense
            key={index}
            onClick={() =>
              onDownloadMenuClickHandler(downloadOption.dispatchId, downloadOption.mode)
            }
          >
            <Typography variant="inherit">{downloadOption.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Card>
  )
}
